/**
 * Main App Component
 * Migrated to NEX Hub (modern marketplace)
 */
import React, { Suspense, lazy, useEffect } from 'react'
import { Routes, Route, Navigate, useLocation } from 'react-router-dom'
import analytics from './lib/analytics'
import ProtectedRoute from './components/auth/ProtectedRoute'

// Core components (always needed)
import HubLayout from './components/hub/HubLayout.jsx'

// Auth pages (NOT lazy - need immediate)
import SignupPage from './pages/auth/SignupPage'
import LoginPage from './pages/auth/LoginPage'
import ForgotPasswordPage from './pages/auth/ForgotPasswordPage'

// Lazy load all other pages
const HubHome = lazy(() => import('./pages/hub/HubHome.jsx'))
const HubBrowse = lazy(() => import('./pages/hub/HubBrowse.jsx'))
const HubAgentDetail = lazy(() => import('./pages/hub/HubAgentDetail.jsx'))
const HubInstallGuide = lazy(() => import('./pages/hub/HubInstallGuide.jsx'))
const HubDocs = lazy(() => import('./pages/hub/HubDocs.jsx'))
const HubQuickStart = lazy(() => import('./pages/hub/HubQuickStart.jsx'))
const HubUserGuide = lazy(() => import('./pages/hub/HubUserGuide.jsx'))

// Admin pages (lazy loaded)
const AdminDashboard = lazy(() => import('./pages/admin/Dashboard.jsx'))
const AdminAgents = lazy(() => import('./pages/admin/Agents.jsx'))
const AdminUsers = lazy(() => import('./pages/admin/Users.jsx'))
const AdminAnalytics = lazy(() => import('./pages/admin/Analytics.jsx'))
const AdminSettings = lazy(() => import('./pages/admin/Settings.jsx'))

// Manager pages (lazy loaded)
const ProjectManager = lazy(() => import('./components/nex-manager/ProjectManager.jsx'))
const AIConfigurator = lazy(() => import('./components/nex-manager/AIConfigurator.jsx'))

// Dashboard page (lazy loaded)
const Dashboard = lazy(() => import('./pages/dashboard/Dashboard'))

// Loading component
const LoadingFallback = () => (
  <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center">
    <div className="text-center">
      <div className="relative w-24 h-24 mx-auto mb-6">
        <div className="absolute inset-0 border-4 border-purple-500/30 rounded-full"></div>
        <div className="absolute inset-0 border-4 border-transparent border-t-purple-500 rounded-full animate-spin"></div>
      </div>
      <div className="text-white text-lg font-semibold mb-2">Loading...</div>
      <div className="text-gray-400 text-sm">Preparing your experience</div>
    </div>
  </div>
)

function App() {
  const location = useLocation()

  // Initialize analytics on mount
  useEffect(() => {
    analytics.init()
  }, [])

  // Track page views on route change
  useEffect(() => {
    analytics.trackPageView(location.pathname)
  }, [location])

  return (
    <div className="app">
      <Suspense fallback={<LoadingFallback />}>
        <Routes>
          {/* Auth Routes (PUBLIC) */}
          <Route path="/signup" element={<SignupPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />

          {/* Hub Routes (PROTECTED) - Modern NEX Marketplace */}
          <Route path="/" element={<Navigate to="/hub" replace />} />
          <Route 
            path="/hub" 
            element={
              <ProtectedRoute>
                <HubLayout><HubHome /></HubLayout>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/hub/browse" 
            element={
              <ProtectedRoute>
                <HubLayout><HubBrowse /></HubLayout>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/hub/agent/:agentId" 
            element={
              <ProtectedRoute>
                <HubLayout><HubAgentDetail /></HubLayout>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/hub/install" 
            element={
              <ProtectedRoute>
                <HubLayout><HubInstallGuide /></HubLayout>
              </ProtectedRoute>
            } 
          />
          
          {/* Documentation Routes (PROTECTED) */}
          <Route 
            path="/hub/docs" 
            element={
              <ProtectedRoute>
                <HubLayout><HubDocs /></HubLayout>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/hub/docs/quick-start" 
            element={
              <ProtectedRoute>
                <HubLayout><HubQuickStart /></HubLayout>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/hub/docs/user-guide" 
            element={
              <ProtectedRoute>
                <HubLayout><HubUserGuide /></HubLayout>
              </ProtectedRoute>
            } 
          />

          {/* Legacy Store Routes - Redirect to Hub */}
          <Route path="/store" element={<Navigate to="/hub" replace />} />
          <Route path="/store/*" element={<Navigate to="/hub/browse" replace />} />

          {/* Dashboard Route (PROTECTED) */}
          <Route 
            path="/dashboard" 
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            } 
          />

          {/* Admin Routes (PROTECTED) */}
          <Route 
            path="/admin" 
            element={
              <ProtectedRoute>
                <AdminDashboard />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/admin/agents" 
            element={
              <ProtectedRoute>
                <AdminAgents />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/admin/users" 
            element={
              <ProtectedRoute>
                <AdminUsers />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/admin/analytics" 
            element={
              <ProtectedRoute>
                <AdminAnalytics />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/admin/settings" 
            element={
              <ProtectedRoute>
                <AdminSettings />
              </ProtectedRoute>
            } 
          />

          {/* Manager Routes (PROTECTED) */}
          <Route 
            path="/manager" 
            element={
              <ProtectedRoute>
                <ProjectManager />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/manager/ai-config" 
            element={
              <ProtectedRoute>
                <AIConfigurator />
              </ProtectedRoute>
            } 
          />

          {/* Default redirect */}
          <Route path="*" element={<Navigate to="/hub" replace />} />
        </Routes>
      </Suspense>
    </div>
  )
}

export default App

